// src/util/money.ts
//
// Money as the backend keeps it: an integer number of øre, excl. VAT, DKK
// (EE-253). Kroner are only ever a string at the edges - what a buyer types
// and what the page shows - so no amount goes through a float on its way to
// the API: "12,10" is 1210 øre, not 12.1 * 100 = 1209.9999999999998.

/** 123450 -> "1.234,50 kr." - the backend's format_cents_dk, byte for byte. */
export const formatCents = (cents: number): string => {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(cents));
  const kroner = Math.floor(abs / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const ore = (abs % 100).toString().padStart(2, "0");
  return `${sign}${kroner},${ore} kr.`;
};

/** Like formatCents, but "-" for an amount that has not been set. */
export const formatCentsOrDash = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? "-" : formatCents(cents);

/** 123450 -> "1234,50", the way it is typed back into an input. */
export const centsToInput = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return "";
  const ore = cents % 100;
  const kroner = Math.floor(cents / 100);
  return ore === 0 ? `${kroner}` : `${kroner},${ore.toString().padStart(2, "0")}`;
};

export type ParsedCents =
  | { ok: true; cents: number | null }
  | { ok: false; error: string };

// A plain amount with at most two decimals, either separator: "1234,5".
const PLAIN = /^(\d+)(?:[.,](\d{1,2}))?$/;
// Thousands grouped the Danish way, "1.234.567,50", or the English way,
// "1,234,567.50". "1.234" is thousands - two decimals is the most an amount
// has. "1,234" is refused rather than guessed at: to a Danish buyer the comma
// is the decimal point, so the English reading needs its decimals to be sure.
const DANISH = /^(\d{1,3}(?:\.\d{3})+)(?:,(\d{1,2}))?$/;
const ENGLISH = /^(\d{1,3}(?:,\d{3})+)\.(\d{1,2})$/;

/**
 * What a buyer typed in kroner, as øre. Empty is `null` - not agreed yet -
 * and is not an error. A trailing "kr." / "DKK" is allowed; a negative
 * amount or a third decimal is not.
 */
export const parseKrToCents = (text: string): ParsedCents => {
  const cleaned = text
    .replace(/\s/g, "")
    .replace(/(kr\.?|dkk)$/i, "");
  if (cleaned === "") return { ok: true, cents: null };
  const match =
    cleaned.match(PLAIN) ?? cleaned.match(DANISH) ?? cleaned.match(ENGLISH);
  if (!match) {
    return { ok: false, error: "Not an amount - e.g. 1234,50" };
  }
  const kroner = Number(match[1].replace(/[.,]/g, ""));
  const ore = Number((match[2] ?? "").padEnd(2, "0"));
  const cents = kroner * 100 + ore;
  if (!Number.isSafeInteger(cents)) {
    return { ok: false, error: "Amount too large" };
  }
  return { ok: true, cents };
};
